import React, { Fragment, cloneElement } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { useFormRef } from "theme/components/atoms/Form/Form/Form";

const STATE_DISABLED = "disabled";
const STATE_PENDING = "pending";

const SubmitWrapper = ({ children, onClick = undefined }) => {
  const formRef = useFormRef();
  return cloneElement(children, {
    onClick: (event) => {
      if (onClick) {
        return onClick(event);
      } else {
        event.stopPropagation();
        event.preventDefault();
        formRef.current.submit(event);
      }
    },
  });
};

export const makeButtonClasses = (
  appearance,
  size = "default",
  state,
  className,
  format
) =>
  classNames({
    [`button`]: appearance !== "icon-none" && appearance !== "none",
    [`button--${appearance}`]: appearance,
    [`button--size-${size}`]: size,
    [`button--state-${state}`]: state,
    [className]: className,
    [`button--format-${format}`]: format,
  });

const Button = ({
  onClick = () => {},
  onMouseEnter = () => {},
  onMouseLeave = () => {},
  onDisableClick = () => {},
  children,
  appearance = "default",
  icon = null,
  format = null,
  size = "default",
  state = "default",
  disabled = false,
  pending = false,
  dangerouslyIgnoreOnClick = false,
  type = "button",
  title = "",
  noSpan = false,
  extraClasses = "",
  ...rest
}) => {
  if (
    process.env.NODE_ENV !== "production" &&
    state === STATE_DISABLED &&
    type !== "submit" &&
    !onDisableClick
  ) {
    console.warn(
      "You should tell the user why the button is disabled if he still click on it",
      children
    );
  }

  if (
    process.env.NODE_ENV === "development" &&
    typeof onClick === "function" &&
    type === "submit"
  ) {
    console.warn("You can't use `onClick` on a submit button", children);
  }

  if (
    process.env.NODE_ENV === "development" &&
    state !== STATE_DISABLED &&
    typeof onClick !== "function" &&
    type !== "submit" &&
    !dangerouslyIgnoreOnClick
  ) {
    console.warn("You must use `onClick` on a button", children);
  }

  const onClickBasedOnState =
    state === STATE_DISABLED
      ? (e) => {
          e.preventDefault();
          onDisableClick && onDisableClick(e);
        }
      : onClick;

  const ButtonWrapper = type === "submit" ? SubmitWrapper : Fragment;
  const wrapperProps =
    type === "submit"
      ? { children, onClick: onClickBasedOnState }
      : { children };

  return (
    <ButtonWrapper {...wrapperProps}>
      <button
        {...rest}
        onClick={type !== "submit" ? onClickBasedOnState : undefined}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        className={makeButtonClasses(
          appearance,
          size,
          state,
          extraClasses,
          format
        )}
        type={type}
        aria-label={title}
        disabled={
          ((state === STATE_DISABLED || state === STATE_PENDING) &&
            !onDisableClick) ||
          disabled
        }
      >
        {children}
      </button>
    </ButtonWrapper>
  );
};

Button.propTypes = {
  onClick: PropTypes.func,
  onMouseLeave: PropTypes.func,
  onMouseEnter: PropTypes.func,
  onDisableClick: PropTypes.func,
  children: PropTypes.node.isRequired,
  appearance: PropTypes.string,
  icon: PropTypes.node,
  size: PropTypes.string,
  state: PropTypes.string,
  disabled: PropTypes.bool,
  pending: PropTypes.bool,
  dangerouslyIgnoreOnClick: PropTypes.bool,
  type: PropTypes.oneOf(["button", "submit", "reset"]),
  title: PropTypes.string,
  noSpan: PropTypes.bool,
  extraClasses: PropTypes.string,
};

Button.defaultProps = {
  appearance: "default",
  size: "default",
  state: "default",
  type: "button",
  extraClasses: "",
};

export default Button;
